import { useState } from "react";
import { blogApi, BlogCategoryApi, useBlogCategories } from "api/shop";
import { useToaster } from "utils";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaModal } from "components/molecules";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { MdEdit, MdDelete } from "react-icons/md";
import { generateSlug } from "./Forms/BlogPostForm";

interface BlogCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const inputClassName =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500";

export const BlogCategoriesModal = ({ isOpen, onClose }: BlogCategoriesModalProps) => {
  const { categories, isLoading, invalidate } = useBlogCategories();
  const { showSuccess, showError } = useToaster();

  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");

  const handleNewNameBlur = () => {
    if (!newSlug && newName) {
      setNewSlug(generateSlug(newName));
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setIsSaving(true);
    try {
      await blogApi.createBlogCategory({
        name: newName.trim(),
        slug: newSlug.trim() || undefined,
      });
      showSuccess("Dodano kategorię");
      setNewName("");
      setNewSlug("");
      invalidate();
    } catch (e) {
      showError("Nie udało się dodać kategorii");
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (category: BlogCategoryApi) => {
    setEditingId(category.id);
    setEditName(category.name);
    setEditSlug(category.slug);
  };

  const handleUpdate = async () => {
    if (editingId === null || !editName.trim() || !editSlug.trim()) return;
    setIsSaving(true);
    try {
      await blogApi.updateBlogCategory(editingId, { name: editName.trim(), slug: editSlug.trim() });
      showSuccess("Zaktualizowano kategorię");
      setEditingId(null);
      invalidate();
    } catch (e) {
      showError("Nie udało się zaktualizować kategorii");
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (category: BlogCategoryApi) => {
    if (!confirm(`Czy na pewno chcesz usunąć kategorię "${category.name}"?`)) return;
    try {
      await blogApi.deleteBlogCategory(category.id);
      showSuccess("Usunięto kategorię");
      invalidate();
    } catch (e) {
      showError("Nie udało się usunąć kategorii");
      console.error(e);
    }
  };

  return (
    <PaziaModal id="blog-categories-modal" onCloseRequest={onClose} isOpen={isOpen} size="2xl">
      {isOpen && (
        <ModalPadding>
          <ModalHeader>Kategorie bloga</ModalHeader>

          {isLoading ? (
            <PaziaLoader position="center" />
          ) : (
            <div className="space-y-2">
              {categories.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">Brak kategorii.</p>
              )}
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-800"
                >
                  {editingId === category.id ? (
                    <>
                      <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Nazwa"
                          className={inputClassName}
                        />
                        <input
                          type="text"
                          value={editSlug}
                          onChange={(e) => setEditSlug(e.target.value)}
                          placeholder="Slug"
                          className={inputClassName}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                        >
                          Anuluj
                        </button>
                        <button
                          type="button"
                          onClick={handleUpdate}
                          disabled={isSaving || !editName.trim() || !editSlug.trim()}
                          className="rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Zapisz
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <span className="font-medium text-slate-800 dark:text-slate-100">{category.name}</span>
                        <code className="ml-2 rounded bg-slate-100 px-2 py-1 text-sm dark:bg-slate-700">
                          /{category.slug}
                        </code>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEdit(category)}
                          className="rounded p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-indigo-600 dark:hover:bg-slate-600 dark:hover:text-indigo-400"
                          title="Edytuj"
                        >
                          <MdEdit size={20} />
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          className="rounded p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-red-600 dark:hover:bg-slate-600 dark:hover:text-red-400"
                          title="Usuń"
                        >
                          <MdDelete size={20} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 border-t border-slate-200 pt-4 dark:border-slate-600">
            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">Dodaj kategorię</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onBlur={handleNewNameBlur}
                placeholder="Nazwa"
                className={inputClassName}
              />
              <input
                type="text"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value)}
                placeholder="Slug (generowany z nazwy)"
                className={inputClassName}
              />
            </div>
            <div className="mt-3 flex justify-end">
              <PaziaButton
                type="primary"
                title="Dodaj kategorię +"
                onClick={handleAdd}
                disabled={isSaving || !newName.trim()}
              />
            </div>
          </div>
        </ModalPadding>
      )}
    </PaziaModal>
  );
};
